import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, onSnapshot, orderBy, doc } from 'firebase/firestore';
import { functions, db } from '../firebase';
import { useApp } from '../context/AppContext';
import { saveGoogleCalendarColors, getGoogleCalendarVisibility } from '../config/calendarColors';
import { devLog, devError } from '../utils/debugLogger';

export const useGoogleCalendar = () => {
  const { user, setGoogleCalendars, setGoogleCalendarEvents, googleCalendarConnected } = useApp();
  const [events, setEvents] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Track if we've validated the token this session
  const tokenValidatedRef = useRef(false);

  // Validate token when user connects (check if token was revoked externally)
  useEffect(() => {
    if (!user || !googleCalendarConnected || tokenValidatedRef.current) {
      return;
    }

    const validateToken = async () => {
      devLog('[GoogleCalendar] Validating token on mount...');
      try {
        const validateFn = httpsCallable(functions, 'validateGoogleToken');
        const result = await validateFn();
        devLog('[GoogleCalendar] Token validation result:', result.data);
        
        if (result.data.revoked) {
          devLog('[GoogleCalendar] Token was revoked externally');
          // The Cloud Function already marked user as disconnected
          // Firestore listener will pick up the change and show the prompt
        }
        
        tokenValidatedRef.current = true;
      } catch (error) {
        devError('[GoogleCalendar] Token validation error:', error);
        // Don't mark as validated so we can retry later
      }
    };

    validateToken();
  }, [user, googleCalendarConnected]);

  // Subscribe to Firestore events (only when connected)
  useEffect(() => {
    if (!user) {
      setEvents([]);
      setLoading(false);
      return;
    }
    
    // If not connected, don't show Google events
    if (!googleCalendarConnected) {
      setEvents([]);
      setGoogleCalendarEvents([]); // Clear AppContext too
      setCalendars([]);
      setLoading(false);
      return;
    }

    // Note: We don't use orderBy here because all-day events have startTime=null
    // and Firestore orderBy excludes documents with null values
    // We sort client-side instead
    const q = query(
      collection(db, 'users', user.userId, 'google_events')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newEvents = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const startTimeDate = data.startTime ? new Date(data.startTime) : null;
        const endTimeDate = data.endTime ? new Date(data.endTime) : null;
        
        // Derive date string for filtering - use startTime for timed events, date for all-day
        let dateStr = data.date; // All-day events already have date as "YYYY-MM-DD"
        if (!dateStr && startTimeDate) {
          // For timed events, derive date from startTime
          dateStr = `${startTimeDate.getFullYear()}-${String(startTimeDate.getMonth() + 1).padStart(2, '0')}-${String(startTimeDate.getDate()).padStart(2, '0')}`;
        }
        
        // Derive endDate for timed multi-day events if not already set
        let endDateStr = data.endDate || null;
        if (!endDateStr && endTimeDate && startTimeDate) {
          const startDay = `${startTimeDate.getFullYear()}-${String(startTimeDate.getMonth() + 1).padStart(2, '0')}-${String(startTimeDate.getDate()).padStart(2, '0')}`;
          const endDay = `${endTimeDate.getFullYear()}-${String(endTimeDate.getMonth() + 1).padStart(2, '0')}-${String(endTimeDate.getDate()).padStart(2, '0')}`;
          if (startDay !== endDay) {
            endDateStr = endDay;
          }
        }
        
        // Convert Date objects to time strings (HH:mm) for EventsPage compatibility
        const startTimeStr = startTimeDate 
          ? `${String(startTimeDate.getHours()).padStart(2, '0')}:${String(startTimeDate.getMinutes()).padStart(2, '0')}`
          : null;
        const endTimeStr = endTimeDate
          ? `${String(endTimeDate.getHours()).padStart(2, '0')}:${String(endTimeDate.getMinutes()).padStart(2, '0')}`
          : null;
        
        return {
          id: docSnap.id,
          ...data,
          // Flag this as a Google Calendar event for edit/update logic
          isGoogleEvent: true,
          // Ensure googleEventId is explicitly available (from data.googleEventId)
          googleEventId: data.googleEventId,
          // Normalize 'title' to 'name' for EventsPage compatibility
          name: data.title || '(No Title)',
          // Time strings for display/sorting (matches local event format)
          startTime: startTimeStr,
          endTime: endTimeStr,
          // Keep full Date objects if needed
          startDateTime: startTimeDate,
          endDateTime: endTimeDate,
          // Use string date for filtering (matches EventsPage format)
          date: dateStr,
          // End date for multi-day events (including timed multi-day)
          endDate: endDateStr,
          // Flag for timed multi-day events (show as bar but with times)
          isMultiDayTimed: data.isMultiDayTimed || (endDateStr && endDateStr !== dateStr && !data.isAllDay),
          // Ensure description is available
          description: data.description || '',
        };
      });
      if (newEvents.length > 0) {
        // Log any multi-day events
        const multiDay = newEvents.filter(e => e.endDate && e.endDate !== e.date);
        if (multiDay.length > 0) {
        } else {
        }
      }
      setEvents(newEvents);
      setGoogleCalendarEvents(newEvents); // Sync to AppContext for stats calculation
      setLoading(false);
    }, (error) => {
      devError("Error fetching Google events:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, googleCalendarConnected, setGoogleCalendarEvents]);

  // Subscribe to calendar list (only when connected)
  useEffect(() => {
    if (!user) {
      setCalendars([]);
      return;
    }
    
    // If not connected, don't subscribe to calendars
    if (!googleCalendarConnected) {
      setCalendars([]);
      setGoogleCalendars([]);
      return;
    }

    const calendarDocRef = doc(db, 'users', user.userId, 'google_sync', 'calendars');

    const unsubscribe = onSnapshot(calendarDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const calendarList = data.calendars || [];
        
        // Save colors to localStorage for quick access by getEventColor()
        saveGoogleCalendarColors(calendarList);
        
        // Apply saved visibility from localStorage
        const savedVisibility = getGoogleCalendarVisibility();
        const calendarsWithVisibility = calendarList.map(cal => ({
          ...cal,
          visible: savedVisibility[cal.id] !== undefined ? savedVisibility[cal.id] : (cal.visible !== false)
        }));
        
        setCalendars(calendarsWithVisibility);
        // Also update the AppContext googleCalendars state
        setGoogleCalendars(calendarsWithVisibility);
      } else {
        setCalendars([]);
        setGoogleCalendars([]);
      }
    }, (error) => {
    });

    return () => unsubscribe();
  }, [user, googleCalendarConnected, setGoogleCalendars]);

  // Connect to Google Calendar
  const connect = useCallback(async () => {
    try {
      const getUrlFn = httpsCallable(functions, 'getGoogleAuthURL');
      const result = await getUrlFn();
      window.location.href = result.data.url;
    } catch (error) {
      throw error;
    }
  }, []);

  // Manual Sync. A ref, not the `syncing` state, guards against overlap, so
  // `sync` keeps one identity and the Calendar page's timer isn't reset by it
  const syncingRef = useRef(false);
  const sync = useCallback(async () => {
    if (syncingRef.current) {
      devLog('[GoogleCalendar] Sync already in progress, skipping');
      return null;
    }
    syncingRef.current = true;
    setSyncing(true);
    try {
      const syncFn = httpsCallable(functions, 'syncGoogleCalendar');
      const result = await syncFn();
      devLog('[GoogleCalendar] Sync complete:', result.data);
      return result.data;
    } catch (error) {
      devError('[GoogleCalendar] Sync error:', error);
      return null;
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, []);

  // CRUD Operations
  const createEvent = useCallback(async (event, calendarId) => {
    const fn = httpsCallable(functions, 'createGoogleEvent');
    const result = await fn({
      event,
      calendarId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    await sync(); // Refresh local cache
  }, [sync]);

  const updateEvent = useCallback(async (eventId, event, calendarId) => {
    const fn = httpsCallable(functions, 'updateGoogleEvent');
    const result = await fn({ eventId, event, calendarId });
    await sync();
  }, [sync]);

  const deleteEvent = useCallback(async (eventId, calendarId) => {
    const fn = httpsCallable(functions, 'deleteGoogleEvent');
    const result = await fn({ eventId, calendarId });
    // No need to sync, we deleted from cache in backend
  }, []);

  return {
    events,
    calendars,
    loading,
    syncing,
    connect,
    sync,
    createEvent,
    updateEvent,
    deleteEvent
  };
};
